import type { Theme } from '@emotion/react';
import type { BrandName } from 'packages/types/brand-name';
import { getColorTones } from '../utils';
import baseTheme from './tokens';

interface BrandFont {
  family: string;
  url: string;
}

interface BrandTheme {
  brandOne: string;
  brandTwo: string;
  utilityBackground: string;
  utilityText: string;
  headingFont?: BrandFont;
  event?: {
    brandTwo?: string;
  };
}

// Add an entry here to make a CMS brand themeable. Brands without one stay on the Agria theme.
export const brandThemes: Partial<Record<BrandName, BrandTheme>> = {
  BASC: {
    brandOne: '#078463',
    brandTwo: '#014711',
    utilityBackground: '#E6F3EF',
    utilityText: '#3C4858',
    headingFont: {
      family: 'Roboto Slab, serif',
      url: 'https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@400;600&display=swap',
    },
    event: {
      brandTwo: '#12452D',
    },
  },
  GCCF: {
    brandOne: '#452683',
    brandTwo: '#C29C5E',
    utilityBackground: '#ECE9F3',
    utilityText: '#452683',
  },
};

interface BrandThemeOptions {
  brand?: BrandName | null;
  isEvent?: boolean;
}

const getBrandConfig = (brand?: BrandName | null) =>
  brand ? brandThemes[brand] : undefined;

const withHeadingFont = (
  typography: typeof baseTheme.typography,
  { family: fontFamily }: BrandFont,
) => {
  const regular = { fontFamily, fontWeight: 400 };
  const bold = { fontFamily, fontWeight: 600 };

  return {
    ...typography,
    headingXLarge: { ...typography.headingXLarge, ...bold },
    headingLarge: { ...typography.headingLarge, ...bold },
    headingMedium: { ...typography.headingMedium, ...bold },
    headingSmall: { ...typography.headingSmall, ...bold },
    bodyLarge: { ...typography.bodyLarge, ...regular },
    bodyLargeBold: { ...typography.bodyLargeBold, ...bold },
    quote: { ...typography.quote, ...bold },
  };
};

export const createBrandTheme = ({
  brand,
  isEvent = false,
}: BrandThemeOptions = {}): Theme => {
  const config = getBrandConfig(brand);

  if (!config) {
    return baseTheme;
  }

  const brandOne = getColorTones(config.brandOne, 'brandOne');
  const brandTwo = getColorTones(
    (isEvent && config.event?.brandTwo) || config.brandTwo,
    'brandTwo',
  );

  // Cast needed because brand overrides widen the base theme's literal colour types.
  return {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...brandOne,
      ...brandTwo,
      utilityBackground: config.utilityBackground,
      utilityText: config.utilityText,
      ...(isEvent
        ? {
            // Divider tokens (default + inverse) sit on brandOne100
            brandOne10: brandOne.brandOne80,
            brandOne50: brandOne.brandOne80,
            brandOne20: baseTheme.colors.brandOne20,
          }
        : {}),
    },
    ...(!isEvent && config.headingFont
      ? {
          typography: withHeadingFont(baseTheme.typography, config.headingFont),
        }
      : {}),
  } as Theme;
};

export const getBrandFontUrl = ({
  brand,
  isEvent = false,
}: BrandThemeOptions = {}) =>
  isEvent ? undefined : getBrandConfig(brand)?.headingFont?.url;
